import type {
  AssetDataMachineData,
  CounterLog,
  MachineStatusLog,
  MaintenanceCreateInput,
  MaintenanceLog,
  MoveModuleLog,
  MutationEvent,
  OperationAmosLight,
  OperationMachine,
  OperationMission,
  OperationModule,
  OperationSpl,
  OperationStock,
  OperationStore,
  OperationTask,
  ResetOverdueLog,
  StockLog,
} from '@gmao/types';
import type { Table } from 'dexie';
import Dexie from 'dexie';

export interface OperationFile {
  path: string;
  blob: Blob;
  createdAt: string;
}

export interface MachineDoc {
  id: number;
  updatedAt: string;
  file: Blob;
}

export interface MachineModel {
  name: string;
  files: {
    path: string;
    updatedAt: string;
    data: Blob;
  }[];
}

export interface MaintenanceLogDraft extends MaintenanceCreateInput {
  eventId: string;
}

export interface AmosCatalogState {
  lastUpdatedAt: string;
  count: number;
}

// What the server answered when it refused the event. Not indexed: no schema version needed
export interface EventRejection {
  code?: string;
  message?: string;
}

export type LocalMutationEvent = MutationEvent & { rejection?: EventRejection };

export class Idb extends Dexie {
  public missions!: Table<OperationMission>;
  public machines!: Table<OperationMachine>;
  public tasks!: Table<OperationTask>;
  public stores!: Table<OperationStore>;
  public spls!: Table<OperationSpl>;
  public nomenclatures!: Table<AssetDataMachineData & { id: number }>;
  // Device-wide AMOS catalog, shared by all missions (see kv `amosCatalog`)
  public amos!: Table<OperationAmosLight>;

  public modules!: Table<OperationModule>;
  public stocks!: Table<OperationStock>;

  public mutationsEvents!: Table<LocalMutationEvent>;
  public maintenances!: Table<MaintenanceLog>;
  public maintenancesDrafts!: Table<MaintenanceLogDraft>;
  public moves!: Table<MoveModuleLog>;
  public stocksLogs!: Table<StockLog>;
  public countersLogs!: Table<CounterLog>;
  public resetOverdueLogs!: Table<ResetOverdueLog>;
  public machineStatusLogs!: Table<MachineStatusLog>;

  public files!: Table<OperationFile>;
  public nomenclaturesDocs!: Table<MachineDoc>;
  public models!: Table<MachineModel>;

  constructor() {
    super('operations');

    this.version(28).stores({
      missions: '&id',
      machines: '&id',
      tasks: '&id',
      stores: '&id',
      spls: '&id',
      nomenclatures: '&id',

      modules: '&serialNumber,amosId,emplacementId,funcLocationId',
      stocks: '[emplacementId+amosId],storeId,amosId,emplacementId',

      mutationsEvents:
        '&id,type,missionId,syncAt,logDate,taskId,machineId,funcLocationId,moduleSerialNumber,storeId,emplacementId',
      maintenances: '&eventId,id,missionId,taskId,funcLocationId,moduleSerialNumber,emplacementId',
      maintenancesDrafts: '&eventId',
      moves:
        '&eventId,id,missionId,moduleSerialNumber,to.funcLocationId,to.emplacementId,from.funcLocationId,from.emplacementId',
      stocksLogs: '&eventId,id,amosId,emplacementId,storeId,maintenanceLogEventId',
      countersLogs: '&eventId,id,funcLocationId,moduleSerialNumber',
      resetOverdueLogs: '&eventId,id,funcLocationId,taskId,moduleSerialNumber',
      machineStatusLogs: '&eventId,id,machineId,status',

      files: '&path',
      nomenclaturesDocs: '&id,updatedAt',
      models: '&name',
    });

    this.version(29).stores({
      amos: '&id,code',
    });
  }
}

export const idb = new Idb();
